// Enhanced Comment.js with full functionality (self-contained)
// This file replaces the need for passing props for comment, audio, media, etc.

import React, { useState, useEffect, useRef } from "react";
import { Avatar } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MicIcon from "@mui/icons-material/Mic";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import axios from "axios";

const Comment = ({ setActiveAssetId, setCaption, setIsCommentModalOpen, commentSubmitted, post }) => {
  const [comment, setComment] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [media, setMedia] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setMedia(file);
    if (file.type.startsWith("audio")) {
      const preview = URL.createObjectURL(file);
      setAudioUrl(preview);
    }
  };

  const handleAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setAudioUrl(preview);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post("https://stt.tiptopbest.com/upload", formData);
      if (res.data?.transcript) {
        setComment(res.data.transcript);
      }
    } catch (err) {
      console.error("Transcription error", err);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        audioChunksRef.current = [];
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Mic access error", err);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const playAudio = () => {
    audioRef.current?.play();
  };

  const handleSend = async () => {
    if (!comment && !media && !audioUrl) return;

    try {
      const token = localStorage.getItem("authToken");

      if (media) {
        const formData = new FormData();
        formData.append("assetID", post.AssetID);
        formData.append("feedID", post.FeedID);
        formData.append("media", media);
        formData.append("comment", comment);

        await axios.post("https://genz-staging.feeltiptop.com/api/media_response", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else if (comment) {
        // THIS IS HARDCODED FOR TESTING PURPOSES
        await axios.post(
          "https://genz-staging.feeltiptop.com/api/comment",
          {
            assetID: post.AssetID,
            feedID: 1,
            comment,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      setComment("");
      setMedia(null);
      setAudioUrl(null);
    } catch (err) {
      console.error("Submission error", err);
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <div className="flex items-center gap-3">
        <Avatar alt="User Avatar" src="/user-avatar.jpg" sx={{ width: 36, height: 36 }} />
        <input
          type="text"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Leave some comments..."
          className="flex-grow rounded-full border border-gray-300 bg-white px-4 py-2 text-sm focus:outline-none"
        />
        <button
          className="rounded-full bg-blue-600 px-4 py-1 text-white hover:bg-blue-700"
          onClick={handleSend}
        >
          Post
        </button>
      </div>

      <div className="ml-11 flex gap-3">
        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
        <input type="file" ref={audioInputRef} className="hidden" onChange={handleAudioUpload} accept="audio/*" />

        <button onClick={() => fileInputRef.current.click()} className="flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100">
          <InsertDriveFileIcon fontSize="small" /> Add File
        </button>

        <button onClick={isRecording ? stopRecording : startRecording} className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm ${isRecording ? "bg-red-100 text-red-600" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}>
          <MicIcon fontSize="small" /> {isRecording ? "Stop" : "Record Audio"}
        </button>

        <button onClick={() => audioInputRef.current.click()} className="flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100">
          <GraphicEqIcon fontSize="small" /> Add Audio
        </button>

        {audioUrl && (
          <button onClick={playAudio} className="flex items-center gap-1 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 hover:bg-green-100">
            <PlayCircleIcon fontSize="small" /> Play Audio
          </button>
        )}
      </div>
      {audioUrl && <audio ref={audioRef} src={audioUrl} hidden />} 
    </div>
  );
};

export default Comment;
